import React, { useState,useEffect, useRef, useMemo } from "react";
import { useNavigate, useParams, useLocation  } from "react-router-dom";
import UploadIcon from "@mui/icons-material/Upload";
import DownloadIcon from "@mui/icons-material/Download";

import { styled, useTheme } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";

import MuiTimeline from "@mui/lab/Timeline";

// ** Custom Component Import
import OptionsMenu from "./option-menu";

import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import ExportToExcel from "./export-components/ExportExcel";
import { exportToCSV } from "./export-components/ExportToCSV";
import { convertESTToLocalDateTime, getCurrentDate } from "./formatDate";
import { GetCustomer } from "screens/Customers/logic/GetCustomer";
import { GetContacts } from "screens/Customers/logic/GetContacts";

// Styled Timeline component
const Timeline = styled(MuiTimeline)({
  "& .MuiTimelineItem-root": {
    width: "100%",
    "&:before": {
      display: "none",
    },
  },
});

// ** Styled Components
const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : theme.palette.common.white,
    minWidth: 220,
    maxWidth: 440,
    border: "1px solid #E1E4EA",
  },
}));
const ImportExportOptions = ({
  handleOpenBulkImportDlg=()=>{},
  // handleExcelExport,
  // handleCSVExport
  data=[],
  module="all",
  customerID
}) => {

  const theme = useTheme();
  const MySwal = withReactContent(Swal);
  const navigate = useNavigate();
  const [customerName, setCustomerName] = useState('');
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    const fetchCustomer = async () => {
      if (customerID > 0) {
        try {
          const customer = await GetCustomer(customerID, navigate);
          const contactsData = await GetContacts(customerID);
          
          // console.log(contactsData);
          setContacts(contactsData); // Store contacts in state
          
          const customersName = customer[0]?.customerType === "Business" 
            ? customer[0]?.companyName 
            : (customer[0]?.firstName + " " + customer[0]?.lastName) || '';
          setCustomerName(customersName);
        } catch (error) {
          console.error("Error fetching customer:", error);
        }
      }
    };
  
    fetchCustomer();
  }, [customerID, navigate]);

  const transformContactsForExport = (contacts) => {
    return contacts.map(contact => {
      // Format all phone numbers as a string
      const phones = contact.phoneNumbers?.map(phone => 
        // `${phone.phoneNo}${phone.isPrimary ? ' (Primary)' : ''}`
        `${phone.phoneNo}`
      ).join(', ') || '';
      
      return {
        id: contact.id,
        contact: contact.contact,
        email: contact.email,
        jobTitle: contact.jobTitle,
        primary: contact.primary ? "Yes" : "No",
        // firstName: contact.firstName,
        // lastName: contact.lastName,
        customerID: contact.customerID,
        customerName: customerName,
        status: contact.status,
        phoneNumbers: phones, // Format as string
      };
    });
  };

  const transformJobsitesForExport = (jobsites) => {
    return jobsites.map(jobsite => {
      // Format all phone numbers as a string
      // const phones = jobsite.contact?.map(phone => 
      //   `contactID: ${phone.contactID}${phone.isPrimary ? ' (Primary)' : ''}`
      // ).join('; ') || '';
      const contactMap = {};
      contacts.forEach(contact => {
        contactMap[contact.id] = contact;
      });

      const phones = jobsite.contact?.map(contactItem => {
        const contactData = contactMap[contactItem.contactID];
        
        if (contactData && contactData.phoneNumbers && contactData.phoneNumbers.length > 0) {
          // Get all phone numbers for this contact, comma separated
          const phoneNumbers = contactData.phoneNumbers.map(phone => 
            `${phone.phoneNo}`
          ).join(', ');
          
          return `${phoneNumbers}`;
        } else {
          // Fallback if contact data not found
          return ``;
        }
      }).join(', ') || '';
      
      return {
        jobsiteID: jobsite.jobsiteID,
        customerID: jobsite.customerID,
        customerName: customerName,
        jobsiteType: jobsite.jobsiteType,
        jobsiteName: jobsite.jobsiteName,
        street1: jobsite.street1,
        street2: jobsite.street2,
        floorLevel: jobsite.floorLevel,
        unitNumber: jobsite.unitNumber,
        city: jobsite.city,
        state: jobsite.state,
        zipCode: jobsite.zipCode,
        county: jobsite.country,
        contact: phones,
        createDate: jobsite.createDate ? convertESTToLocalDateTime(jobsite.createDate) : null,
        createdBy: jobsite.createdBy,
        // modifiedDate: jobsite.modifiedDate,
        // modifiedBy: jobsite.modifiedBy,
      };
    });
  };

  const transformMaterialsForExport = (materials) => {
    return materials.map(material => {      
      return {
        materialID: material.materialID,        
        materialName: material.itemName,
        // materialTypeID: material.materialTypeID,
        materialType: material.materialType,
        unitOfMeasurement: material.unitOfMeasurement,
        costPerUnit: material.costPerUnit,
        perUnitPrice: material.perUnitPrice,
        latestRestockDate: material.latestRestockDate ? convertESTToLocalDateTime(material.latestRestockDate) : null,
        scheduledQuantity: material.scheduledQuantity,
        warehouseQuantity: material.warehouseQuantity,
        quantityInStock: material.quantityInStock,
        lowStockAlert: material.lowStockAlert,
        location: material.location,
        salesTax: material.salesTax,
        chargeSalesTax: material.chargeSalesTax,
        vendors: material.vendors,
        createDate: material.createDate ? convertESTToLocalDateTime(material.createDate) : null,        
        createdBy: material.createdBy,                
      };
    });
  };

  const transformAssetsForExport = (assets) => {
    return assets.map(asset => {      
      return {
        assetID: asset.assetID,        
        assetName: asset.assetName,
        assetType: asset.assetType,
        serialVINNumber: asset.serialVINNumber,
        licensePlate: asset.licensePlate,
        hourlyRate: asset.hourlyRate,
        designatedOperator: asset.designatedOperator,
        operationalCostsAmount: asset.operationalCostsAmount,
        operationalCostsUnit: asset.operationalCostsUnit,
        nextMaintenanceDate: asset.nextMaintenanceDate ? convertESTToLocalDateTime(asset.nextMaintenanceDate) : null,
        lastMaintenanceDate: asset.lastMaintenanceDate ? convertESTToLocalDateTime(asset.lastMaintenanceDate) : null,
        purchaseDate: asset.purchaseDate ? convertESTToLocalDateTime(asset.purchaseDate) : null,
        purchasePrice: asset.purchasePrice,
        warrantyExpiration: (asset.warrantyExpiration && asset.warrantyExpiration !== "1900-01-01T00:00:00") ? convertESTToLocalDateTime(asset.warrantyExpiration) : null,
        gpsTracking: asset.gpsTracking,
        assetLocation: asset.assetLocation,
        status: asset.status,
        createDate: asset.createDate ? convertESTToLocalDateTime(asset.createDate) : null,  
        createdBy: asset.createdBy,    
      };
    });
  };

  const transformPaymentsForExport = (payments) => {
    return payments.map(payment => {      
      return {
        id: payment.PAYpaymentID,
        LinkedTo: payment.WO_ID,
        customerName: payment.customerName,
        // createdBy: payment.createdBy,
        // dueDate: payment.dueDate ? convertESTToLocalDateTime(payment.dueDate) : null,  
        paymentDate: payment.item_paymentDate ? convertESTToLocalDateTime(payment.item_paymentDate) : null,
        paymentMethod: payment.paymentMethod,
        amount: payment.amount,
        // status: payment.status,
        // entity: payment.entity,
        paymentType: payment.paymentType,
        entityID: payment.requestID,
        invoiceID: payment.invoiceID,
        // notes: payment.notes,
        // paymentDate: payment.paymentDate,
        paymentStatus: payment.paymentStatus,
        paidBy: payment.paidBy,
        invoiceTitle: payment.invoiceTitle,
        // customerID: payment.customerID,
        customerEmail: payment.customerEmail,
        requestTitle: payment.requestTitle,
        requestType: payment.requestType,
        // createBy: payment.createBy,    
        createDate: payment.createDate ? convertESTToLocalDateTime(payment.createDate) : null,  
          
      };
    });
  };

  const transformInvoicesForExport = (invoices) => {
    return invoices.map(invoice => {      
      return {
        id: invoice.id,
        // invoiceID: invoice.invoiceID,
        requestID: invoice.requestID,
        requestType: invoice.requestType,
        isDeposit: invoice.isDeposit,
        // depositRequired: invoice.depositRequired,
        invoiceTitle: invoice.invoiceTitle,
        invoiceDate: invoice.invoiceDate ? convertESTToLocalDateTime(invoice.invoiceDate) : null,
        dueDate: invoice.dueDate ? convertESTToLocalDateTime(invoice.dueDate) : null,
        status: invoice.status,
        county: invoice.county,
        subTotal: invoice.subTotal,
        taxAmount: invoice.taxAmount,
        discountAmount: invoice.discountAmount,
        totalAmount: invoice.totalAmount,
        depositUtilizedAmount: invoice.depositUtilizedAmount,
        // notes: invoice.notes,
        paymentMethods: invoice.paymentMethods,
        // additionalPaymentTerms: invoice.additionalPaymentTerms,
        // invoiceJobs: invoice.invoiceJobs,
        // customerID: invoice.customerID,
        customerName: invoice.customerName,
        customerEmail: invoice.customerEmail,
        customerPhone: invoice.customerPhone,
        jobsiteName: invoice.jobsiteName,
        jobsiteAddress: invoice.jobsiteAddress,
        // billingAddress_Street1: invoice.billingAddress_Street1,
        // billingAddress_Street2: invoice.billingAddress_Street2,
        requestTitle: invoice.requestTitle,
        createdBy: invoice.createdBy,       
        createDate: invoice.createDate ? convertESTToLocalDateTime(invoice.createDate) : null,  
          
      };
    });
  };

  const transformServicesForExport = (services) => {
    return services.map(service => {      
      return {
        id: service.id,
        // createDate: service.createDate,
        industryID: service.industryID,
        industryName: service.industryName,
        serviceOfferingName: service.serviceOfferingName,
        description: service.description,
        flatRate: service.flatRate,
        // totalCount: service.totalCount,
        // currentPage: service.currentPage,
        createdBy: service.createdBy,
        createDate: service.createDate,// ? convertESTToLocalDateTime(service.createDate) : null,  
          
      };
    });
  };

  const handleExcelExportByModule = (mydata) =>{
    let transformedData;
    if(module==="Contacts"){
      transformedData = transformContactsForExport(mydata);
    } else if (module === "Jobsites"){
      transformedData = transformJobsitesForExport(mydata);
    } else if (module === "Assets"){
      transformedData = transformAssetsForExport(mydata);
    } else if (module === "Materials"){
      transformedData = transformMaterialsForExport(mydata);
    } else if (module === "Services"){
      transformedData = transformServicesForExport(mydata);
    } else if (module === "Payments"){
      transformedData = transformPaymentsForExport(mydata);
    } else if (module === "Invoices"){
      transformedData = transformInvoicesForExport(mydata);
    } else {
      transformedData = mydata;
    }    

    if(ExportToExcel(transformedData, `${module} [${getCurrentDate()}]`)){
      Swal.fire(
        {
          title: 'Exported!',
          text: 'Data has been successfully exported!',
          icon: 'success',
          confirmButtonColor: '#23a26d', 
        });
      }else{
        Swal.fire(
          {
            title: 'Failed!',
            text: 'No data available to export!',
            icon: 'error',
            confirmButtonColor: '#23a26d', 
          });
      }
  }

  const handleCSVExportByModule = (mydata) => {
    let transformedData;
    if(module==="Contacts"){
      transformedData = transformContactsForExport(mydata);
    } else if (module === "Jobsites"){
      transformedData = transformJobsitesForExport(mydata);
    } else if (module === "Assets"){
      transformedData = transformAssetsForExport(mydata);
    } else if (module === "Materials"){
      transformedData = transformMaterialsForExport(mydata);
    } else if (module === "Services"){
      transformedData = transformServicesForExport(mydata);
    } else if (module === "Payments"){
      transformedData = transformPaymentsForExport(mydata);;
    } else if (module === "Invoices"){
      transformedData = transformInvoicesForExport(mydata);
    } else {
      transformedData = mydata;
    }
    
    if(exportToCSV(transformedData, `${module} [${getCurrentDate()}].csv`, { delimiter: ',', includeHeaders: true })){
      Swal.fire(
            {
              title: 'Exported!',
              text: 'Data has been successfully exported!',
              icon: 'success',
              confirmButtonColor: '#23a26d', 
            });
    }else{
      Swal.fire(
        {
          title: 'Failed!',
          text: 'No data available to export!',
          icon: 'error',
          confirmButtonColor: '#23a26d', 
        });
    }
    
  };

  return (
    <OptionsMenu
      iconButtonProps={{
        sx: { color: "text.secondary", order: { xs: 4, sm: 4 } },
      }}
      options={[
        // {
        //   text: "Import",
        //   menuItemProps: {
        //     onClick: handleOpenBulkImportDlg,
        //   },
        // },
        ...(module === "Contacts" || module === "Jobsites" ? [{
          text: "Import",
          icon: <UploadIcon fontSize="small" sx={{ mr: 1 }}/>,
          menuItemProps: {
            onClick: handleOpenBulkImportDlg,
          },
        }] : []),
        {
          text: "Export",
          icon: <DownloadIcon fontSize="small" sx={{ mr: 1 }}/>,
          menuItemProps: {
          onClick: () => {
            Swal.fire({
            title: '<strong>Export</strong>',
        
            html: `
            <style>
              .swal2-popup{
                background-color: ${theme.palette.mode === 'dark' ? '#1B2431' : '#fff'};
              }
              .exportStyle{
                display: flex; 
                align-items: center; 
                justify-content: center;
              
                gap: 20px;
                height: 150px;
                margin-top: 15px; /* Added for consistent spacing */
              }       
              .custom-close-btn {
                position: absolute;
                right: 10px;
                top: 12px;
                width: 30px;
                height: 30px;
                cursor: pointer;
                z-index: 1000;
                color: #555;
                font-size: 30px;
                line-height: 38px;
                text-align: center;
                background: none;
                border: none;
                padding: 0;
                margin: 0;
              }
              .custom-close-btn:hover {
                color: #333;
              }                     
              .iconBorder{
                border: 1px solid transparent; /* Made border transparent */
                border-radius: 8px;
                padding: 15px;
                cursor: pointer;
                transition: all 0.3s ease;
                margin-top: 15px; /* Adjusted for consistent spacing */
                position: relative;
                width: 130px;
                height: 145px; /* Set to 140px as requested */
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                  box-shadow: 
                0 0 0 1px rgba(0, 0, 0, 0.12), /* Black border effect at 12% opacity */
                0 0 20px #10182808; /* Original shadow */
              }
              .selected{
                border: 2px solid #23a26d !important;
                background-color: #f5fff9 !important;
              }
              .selected::after {
                content: "";
                position: absolute;
                top: 10px;
                right: 10px;
                width: 16px;
                height: 16px;
                border-radius: 50%;
                background-color: #23a26d;
                border: 3px solid white;
                box-shadow: 0 0 0 1px #23a26d;
              }
              .descriptionText{
                text-align:left;
                margin-bottom: 25px;
                margin-top: 20px;
                color: #555;
              }
              .swal2-html-container {
                padding: 0em 1.6em 1.3em !important;
              }
              .exportIcons{
                margin-bottom: 10px;
              }
              .format-label {
                font-size: 14px;
                color: #333;
                margin-top: 8px;
              }
              .swal2-close {
                color: #000 !important;
                font-size: 20px !important;
                width: 24px !important;
                height: 34px !important;
              }
              .swal2-close:hover {
                color: #000 !important;
              }
              .swal2-cancel{
                background: transparent;
                color: #313D4F;
                border: 1px solid #313D4F;
              }
              .swal2-cancel:hover{
                background-color: rgba(49, 61, 79, 0.08) !important;
                border: 1px solid #313D4F;
                background-image: none;
              }
              .swal2-confirm{
                background-color: #23a26d;
              }
              .swal2-confirm:hover{
                background-color: #1d8a5a !important;
              }
            </style>
            <div>
              <button class="custom-close-btn" id="customCloseBtn">×</button>

              <p>Choose your preferred file format to export your data</p>
              <div class="exportStyle">                            
                <div class="iconBorder" id="CSV-Container">
                  <img src="/images/csv.png" class="exportIcons" id="exportCsv" width="50" height="50">
                  <span class="format-label">CSV File</span>                             
                </div>  
                <div class="iconBorder" id="XLSX-Container">
                  <img src="/images/xlsx.png" class="exportIcons" id="exportXlsx" width="50" height="50">
                  <span class="format-label">XLSX File</span>
                </div>                                                            
              </div>
            <div>                          
            `,
            showCancelButton: true,
            confirmButtonText: 'Export',
            cancelButtonText: 'Cancel',
            
            confirmButtonColor: "#23a26d",
            customClass: {
              popup: theme.palette.mode === 'dark' ? 'custom-swal-popup-dark' : 'custom-swal-popup',
          title: theme.palette.mode === 'dark' ? 'custom-swal-title-dark' : 'custom-swal-title',
          htmlContainer: 'custom-swal-html',
          confirmButton: 'custom-swal-confirm',
         cancelButton: theme.palette.mode === 'dark' ? 'custom-swal-cancel-dark' : 'custom-swal-cancel',
          closeButton: 'custom-swal-close'
            },
            showCloseButton: false,
            focusConfirm: false,
            width: "620px",
            didOpen: () => {
              document.getElementById('customCloseBtn').addEventListener('click', () => {
                Swal.close();
              });
              
              const exportButton = document.querySelector(".swal2-confirm");
              exportButton.disabled = true;
              
              document.querySelectorAll(".iconBorder").forEach(option => {
                option.addEventListener("click", function() {
                  document.querySelectorAll(".iconBorder").forEach(opt => 
                    opt.classList.remove("selected"));
                  this.classList.add("selected");
                  exportButton.disabled = false;
                });
              });
            },
            }).then((result) => {
              if (result.isConfirmed) {
                const selected = document.querySelector(".iconBorder.selected");
                // console.log(selected)
                if (selected?.id === "XLSX-Container") {
                  handleExcelExportByModule(data);
                } else {
                  handleCSVExportByModule(data);
                }
              }
            });
          },
        },
      }
      ]}
    />
  )
}

export default ImportExportOptions;